/** Поворот на 90° и обрезка рамкой в просмотрщиках дашборда ОО: фото вейвера (28.08)
 *  и медиа игры (10.10). Правка живёт локально — objectURL JPEG; на сервер её отправляет
 *  вызывающий по «Сохранить», забрав файл через takeFile(). Новый src (сохранили на
 *  сервере / вернули оригинал) сбрасывает локальную правку. */
import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";

export interface CropRect { x: number; y: number; w: number; h: number }

/* iOS Safari не рисует холст больше ~16,7 Мп, а айфон снимает 24 Мп — результат ужимаем
   до этих пределов (для показа игрокам и вейверов — с запасом). */
const MAX_SIDE = 4096;
const MAX_AREA = 16_000_000;
const fitScale = (w: number, h: number): number => Math.min(1, MAX_SIDE / Math.max(w, h), Math.sqrt(MAX_AREA / (w * h)));

interface Plan { w: number; h: number; draw: (ctx: CanvasRenderingContext2D, k: number) => void }

export function usePhotoEdit(src: string | null) {
  const [work, setWork] = useState<string | null>(null);
  const [ops, setOps] = useState<string[]>([]);
  const [cropMode, setCropMode] = useState(false);
  const [sel, setSel] = useState<CropRect | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const dragRef = useRef<{ x: number; y: number } | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const workRef = useRef<string | null>(null);
  workRef.current = work;
  const shown = work ?? src;

  const dropWork = useCallback(() => {
    setWork((prev) => { if (prev) URL.revokeObjectURL(prev); return null; });
    setOps([]); setErr("");
  }, []);
  useEffect(() => { dropWork(); setCropMode(false); setSel(null); }, [src, dropWork]);
  useEffect(() => () => { if (workRef.current) URL.revokeObjectURL(workRef.current); }, []);

  const render = useCallback(async (op: string, plan: (bmp: ImageBitmap) => Plan) => {
    if (!shown) return;
    setBusy(true); setErr("");
    try {
      const blob = await fetch(shown).then((r) => { if (!r.ok) throw new Error(String(r.status)); return r.blob(); });
      const bmp = await createImageBitmap(blob, { imageOrientation: "from-image" });
      const p = plan(bmp);
      const k = fitScale(p.w, p.h);
      const cv = document.createElement("canvas");
      cv.width = Math.max(1, Math.round(p.w * k)); cv.height = Math.max(1, Math.round(p.h * k));
      const ctx = cv.getContext("2d");
      if (!ctx) throw new Error("canvas");
      ctx.imageSmoothingQuality = "high";
      p.draw(ctx, k);
      bmp.close();
      const out = await new Promise<Blob | null>((r) => cv.toBlob(r, "image/jpeg", 0.92));
      if (!out) throw new Error("toBlob");
      setWork((prev) => { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(out); });
      setOps((o) => (o.includes(op) ? o : [...o, op]));
    } catch { setErr("Не получилось обработать фото — попробуй ещё раз"); }
    setBusy(false);
  }, [shown]);

  const rotate = useCallback(() => void render("поворот", (bmp) => ({
    w: bmp.height, h: bmp.width,
    draw: (ctx, k) => {
      ctx.translate((bmp.height * k) / 2, (bmp.width * k) / 2); ctx.rotate(Math.PI / 2); ctx.scale(k, k);
      ctx.drawImage(bmp, -bmp.width / 2, -bmp.height / 2);
    },
  })), [render]);

  const startCrop = useCallback(() => { setCropMode(true); setSel(null); }, []);
  const cancelCrop = useCallback(() => { setCropMode(false); setSel(null); dragRef.current = null; }, []);
  /* рамка — в CSS-пикселях показанного <img>; масштаб к файлу считаем по самому битмапу
     (он уже развёрнут по EXIF, как и картинка на экране) */
  const applyCrop = useCallback(() => {
    const img = imgRef.current;
    const s = sel;
    setCropMode(false); setSel(null);
    if (!img || !s || s.w < 8 || s.h < 8) return;
    const dw = img.clientWidth, dh = img.clientHeight, bx = img.clientLeft, by = img.clientTop;
    void render("обрезка", (bmp) => {
      const kx = bmp.width / dw, ky = bmp.height / dh;
      const sx = Math.max(0, (s.x - bx) * kx), sy = Math.max(0, (s.y - by) * ky);
      const sw = Math.min(bmp.width - sx, s.w * kx), sh = Math.min(bmp.height - sy, s.h * ky);
      return { w: sw, h: sh, draw: (ctx, k) => ctx.drawImage(bmp, sx, sy, sw, sh, 0, 0, Math.round(sw * k), Math.round(sh * k)) };
    });
  }, [sel, render]);

  const relPos = (e: ReactPointerEvent) => {
    const r = imgRef.current?.getBoundingClientRect();
    return r ? { x: Math.min(Math.max(e.clientX - r.left, 0), r.width), y: Math.min(Math.max(e.clientY - r.top, 0), r.height) } : { x: 0, y: 0 };
  };
  const pointer = {
    onPointerDown: (e: ReactPointerEvent<HTMLElement>) => {
      if (!cropMode) return;
      e.preventDefault(); e.currentTarget.setPointerCapture?.(e.pointerId);
      const p = relPos(e); dragRef.current = p; setSel({ x: p.x, y: p.y, w: 0, h: 0 });
    },
    onPointerMove: (e: ReactPointerEvent<HTMLElement>) => {
      if (!cropMode || !dragRef.current) return;
      const p = relPos(e), s0 = dragRef.current;
      setSel({ x: Math.min(s0.x, p.x), y: Math.min(s0.y, p.y), w: Math.abs(p.x - s0.x), h: Math.abs(p.y - s0.y) });
    },
    onPointerUp: () => { dragRef.current = null; },
    onPointerCancel: () => { dragRef.current = null; },
  };

  /** Файл правки для отправки на сервер (null — правок нет). */
  const takeFile = useCallback(async (name: string): Promise<File | null> => {
    if (!work) return null;
    const blob = await fetch(work).then((r) => r.blob());
    return new File([blob], name, { type: "image/jpeg" });
  }, [work]);
  /** Правка сохранена на сервере: objectURL переходит вызывающему (он покажет его как новый src). */
  const commit = useCallback((): string | null => { const w = work; setWork(null); setOps([]); return w; }, [work]);

  return {
    shown, edited: work != null, ops, busy, err, imgRef, cropMode, sel, pointer,
    rotate, startCrop, cancelCrop, applyCrop, discard: dropWork, takeFile, commit,
  };
}
